/**
 * AppShell — the ChatGPT-style chrome every authed page shares (Stage A):
 * a single left sidebar, nothing else.
 *
 * Sidebar anatomy, top to bottom (owner's screenshots, 2026-09-02: the
 * ChatGPT arrangement, product things on top, the conversations under a
 * heading):
 *  - RailHeader: the loremap mark and name (a link to `/`, the last product)
 *    with the collapse toggle beside it. Collapsed, the mark alone IS the
 *    toggle: hovering it shows the expand glyph, clicking expands.
 *  - ProductSwitcher: the product's identity pill + dropdown (all products,
 *    "+ New product", "Manage products"). A tiny accent dot on it means
 *    sessions in OTHER products are writing/unread.
 *  - the product group: "New chat" (→ /products/:pid), "Codebases"
 *    (→ /products/:pid/codebases), "Infrastructure"
 *    (→ /products/:pid/infrastructure) and "Settings"
 *    (→ /products/:pid/settings), icons alone when collapsed;
 *  - SessionList: the product's sessions under a "Recent" heading, newest
 *    activity first, each with its status glyph (spinner while its turn
 *    streams; accent dot when a reply finished off-screen). This region
 *    alone scrolls, and it hides when the rail is collapsed.
 *  - the bottom cluster: RefreshChip, Account, and the theme toggle, which
 *    names the mode it is in ("Dark" / "Light") rather than a bare verb.
 *
 * The active product derives from the URL (`/products/:pid`), falling back to nav
 * memory (shell/lastProduct) and then the first product — so /products and
 * /settings still show the last-visited product's sidebar. RefreshTracker
 * mounts here once (background job streams), the shell unmount aborts every
 * chat stream (logout is the only navigation allowed to do that), and
 * TitleEffect keeps document.title carrying the unread dot.
 */

import { useEffect, type ReactNode } from 'react';
import { Link, matchPath, NavLink, Outlet, useLocation } from 'react-router';

import { useProduct, useProducts } from '../api/queries';
import { handleShellUnmount } from '../features/chat/streamController';
import { RefreshChip, RefreshTracker } from '../features/refresh';
import { LoremapLogo } from '../lib/brand';
import { codebasesPath, infrastructurePath, productPath, productSettingsPath } from '../lib/deepLink';
import { useUiStore } from '../stores/uiStore';
import { useLastProduct } from './lastProduct';
import { ProductSwitcher } from './ProductSwitcher';
import { selectAnyUnread, useSessionActivity } from './sessionActivity';
import { SessionList } from './SessionList';

// ---------------------------------------------------------------------------
// Icons (stroke style shared with the v2 rail)
// ---------------------------------------------------------------------------

const svgProps = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

function NewChatIcon() {
  return (
    <svg {...svgProps} width={16} height={16} strokeWidth={1.8}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function InfrastructureIcon() {
  return (
    <svg {...svgProps}>
      <rect x="3" y="4" width="18" height="6" rx="1.5" />
      <rect x="3" y="14" width="18" height="6" rx="1.5" />
      <path d="M7 7h.01M7 17h.01" />
    </svg>
  );
}

function ProductSettingsIcon() {
  return (
    <svg {...svgProps}>
      <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3" />
      <path d="M2 14h4M10 8h4M18 16h4" />
    </svg>
  );
}

function AccountIcon() {
  return (
    <svg {...svgProps}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5" />
    </svg>
  );
}

function CodebasesIcon() {
  return (
    <svg {...svgProps}>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M10 12l-2 2 2 2M14 12l2 2-2 2" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg {...svgProps}>
      <path d="M21 13A8.5 8.5 0 0 1 11 3a8.5 8.5 0 1 0 10 10z" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg {...svgProps}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2.5M12 19v2.5M2.5 12H5M19 12h2.5M5.3 5.3l1.8 1.8M16.9 16.9l1.8 1.8M5.3 18.7l1.8-1.8M16.9 7.1l1.8-1.8" />
    </svg>
  );
}

function CollapseIcon() {
  return (
    <svg {...svgProps}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

/**
 * document.title names the product and the app; the cross-app unread
 * signal rides the FAVICON (2026-09-03; owner: the bullet in the title "can
 * look better"): while ANY session anywhere holds an unseen reply, the icon
 * links swap to the badged tile, and back when it is seen.
 */
export function applyUnreadIcon(unread: boolean): void {
  if (typeof document === 'undefined') return;
  const svg = document.querySelector('link[rel="icon"][type="image/svg+xml"]');
  const ico = document.querySelector('link[rel="icon"]:not([type])');
  svg?.setAttribute('href', unread ? '/favicon-unread.svg' : '/favicon.svg');
  ico?.setAttribute('href', unread ? '/favicon-unread.ico' : '/favicon.ico');
}

export function TitleEffect({ productName }: { productName: string | null }) {
  const anyUnread = useSessionActivity(selectAnyUnread);
  useEffect(() => {
    // "curium · loremap": the product first, so a row of tabs stays tellable
    // apart where browsers clip titles, and the app named after it.
    document.title = productName === null ? 'loremap' : `${productName} · loremap`;
    applyUnreadIcon(anyUnread);
  }, [anyUnread, productName]);
  // Signing out unmounts the shell: the login page must not keep the badge.
  useEffect(
    () => () => {
      applyUnreadIcon(false);
      document.title = 'loremap';
    },
    [],
  );
  return null;
}

function railRowClass(isActive: boolean, collapsed: boolean): string {
  return `flex items-center ${collapsed ? 'justify-center' : ''} gap-2 rounded-card px-2.5 py-1.5 no-underline transition-colors duration-(--motion-hover) ${
    isActive ? 'bg-accent-subtle text-primary' : 'text-tertiary hover:bg-inset hover:text-primary'
  }`;
}

function RailRow({
  to,
  title,
  label,
  icon,
  collapsed,
  end = false,
  testId,
}: {
  to: string;
  title: string;
  label: string;
  icon: ReactNode;
  collapsed: boolean;
  end?: boolean;
  testId?: string;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      title={title}
      data-testid={testId}
      className={({ isActive }) => railRowClass(isActive, collapsed)}
    >
      <span aria-hidden className="flex h-5 w-5 shrink-0 items-center justify-center">
        {icon}
      </span>
      {!collapsed ? (
        <span className="min-w-0 truncate text-[length:var(--text-ui-size)]">{label}</span>
      ) : null}
    </NavLink>
  );
}

/**
 * The top row: the loremap mark and name (a link home; `/` resolves to the
 * last product, else the products list) with the collapse toggle beside it
 * (owner, 2026-09-02: "Collapse button should live right next to the logo").
 * Collapsed, the mark alone is the toggle: hovering or focusing it swaps in
 * the expand glyph, and clicking expands, the way ChatGPT's rail does.
 * `rail-collapse` is the toggle in both states.
 */
function RailHeader({ collapsed }: { collapsed: boolean }) {
  const toggleRail = useUiStore((s) => s.toggleRail);
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={toggleRail}
        title="Expand the sidebar"
        aria-label="Expand the sidebar"
        aria-expanded={false}
        data-testid="rail-collapse"
        className="group flex h-8 w-full cursor-pointer items-center justify-center rounded-card border-none bg-transparent p-0 text-tertiary transition-colors duration-(--motion-hover) hover:bg-inset hover:text-primary focus-visible:text-primary"
      >
        <span className="flex items-center group-hover:hidden group-focus-visible:hidden">
          <LoremapLogo size={22} hideWordmark />
        </span>
        <span aria-hidden className="hidden items-center group-hover:flex group-focus-visible:flex">
          <CollapseIcon />
        </span>
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1" data-testid="rail-header">
      <Link
        to="/"
        title="loremap"
        data-testid="rail-brand"
        className="flex min-w-0 flex-1 items-center rounded-card px-2 py-1 no-underline transition-colors duration-(--motion-hover) hover:bg-inset"
      >
        <LoremapLogo size={22} />
      </Link>
      <button
        type="button"
        onClick={toggleRail}
        title="Collapse the sidebar"
        aria-label="Collapse the sidebar"
        aria-expanded
        data-testid="rail-collapse"
        className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-card border-none bg-transparent p-0 text-tertiary transition-colors duration-(--motion-hover) hover:bg-inset hover:text-primary"
      >
        <CollapseIcon />
      </button>
    </div>
  );
}

export function AppRail() {
  const location = useLocation();
  const railCollapsed = useUiStore((s) => s.railCollapsed);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const theme = useUiStore((s) => s.theme);
  const products = useProducts();
  const lastProduct = useLastProduct((s) => s.productId);
  const setLastProduct = useLastProduct((s) => s.setLastProduct);

  // The active product comes straight from the URL — deep links, reloads,
  // and direct URLs all resolve identically; off product routes the nav
  // memory (then the first product) keeps the sidebar anchored.
  const match = matchPath({ path: '/products/:pid', end: false }, location.pathname);
  const routePid = match?.params.pid ?? null;
  const knownIds = products.data?.products.map((p) => p.id);
  const activePid =
    routePid ??
    (lastProduct !== null && (knownIds === undefined || knownIds.includes(lastProduct))
      ? lastProduct
      : (knownIds?.[0] ?? null));

  useEffect(() => {
    if (routePid !== null) setLastProduct(routePid);
  }, [routePid, setLastProduct]);

  const activeProduct = useProduct(activePid ?? undefined);

  return (
    <nav
      aria-label="Primary"
      data-testid="app-rail"
      data-collapsed={railCollapsed ? 'true' : 'false'}
      className={`flex shrink-0 flex-col gap-2 border-r border-default bg-surface py-3 ${
        railCollapsed ? 'w-(--rail-width) px-1.5' : 'w-64 px-2.5'
      }`}
    >
      <TitleEffect productName={activeProduct.data?.name ?? null} />
      <RailHeader collapsed={railCollapsed} />
      <ProductSwitcher activeProductId={activePid} railCollapsed={railCollapsed} />

      {activePid !== null ? (
        <div className="flex flex-col gap-0.5" data-testid="rail-product-group">
          <RailRow
            to={productPath(activePid)}
            end
            title="New chat"
            label="New chat"
            icon={<NewChatIcon />}
            collapsed={railCollapsed}
            testId="new-chat"
          />
          <RailRow
            to={codebasesPath(activePid)}
            title="Codebases"
            label="Codebases"
            icon={<CodebasesIcon />}
            collapsed={railCollapsed}
            testId="rail-codebases"
          />
          <RailRow
            to={infrastructurePath(activePid)}
            title="Infrastructure"
            label="Infrastructure"
            icon={<InfrastructureIcon />}
            collapsed={railCollapsed}
            testId="rail-infrastructure"
          />
          <RailRow
            to={productSettingsPath(activePid)}
            title="Product settings"
            label="Settings"
            icon={<ProductSettingsIcon />}
            collapsed={railCollapsed}
            testId="rail-product-settings"
          />
        </div>
      ) : null}

      {activePid !== null && !railCollapsed ? (
        <SessionList productId={activePid} />
      ) : (
        <div className="min-h-0 flex-1" />
      )}

      <div className="flex flex-col gap-0.5 border-t border-default pt-2">
        <RefreshChip />
        <RailRow
          to="/settings"
          title="Account"
          label="Account"
          icon={<AccountIcon />}
          collapsed={railCollapsed}
          testId="rail-account"
        />
        <button
          type="button"
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'}
          data-testid="rail-theme"
          data-theme-mode={theme}
          className={`flex cursor-pointer items-center ${railCollapsed ? 'justify-center' : ''} gap-2 rounded-card border-none bg-transparent px-2.5 py-1.5 text-tertiary transition-colors duration-(--motion-hover) hover:bg-inset hover:text-primary`}
        >
          <span aria-hidden className="flex h-5 w-5 shrink-0 items-center justify-center">
            {theme === 'dark' ? <MoonIcon /> : <SunIcon />}
          </span>
          {!railCollapsed ? (
            <span className="text-[length:var(--text-ui-size)]">
              {theme === 'dark' ? 'Dark' : 'Light'}
            </span>
          ) : null}
        </button>
      </div>
    </nav>
  );
}

export function AppShell() {
  // The authed shell leaving the tree IS logout — the only navigation that
  // aborts chat streams (all of them, silently).
  useEffect(() => () => handleShellUnmount(), []);

  return (
    <div className="flex h-full">
      <AppRail />
      <div className="min-w-0 flex-1 overflow-y-auto">
        <Outlet />
      </div>
      <RefreshTracker />
    </div>
  );
}
